import { useEffect, useState } from "react";
import { format } from "date-fns";
import { toast } from "sonner";
import { Trash2, Shuffle, Sparkles, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { inputCls, btnPrimary, btnSecondary } from "@/components/shared";
import { randomPrompt } from "@/components/diary/prompts";

const PromptPicker = ({ onUse }) => {
  const [p, setP] = useState(() => randomPrompt());
  return (
    <div className="rounded-xl border border-dashed border-t-line bg-t-surface/60 p-4 flex items-start gap-3" data-testid="diary-prompt-picker">
      <Sparkles className="h-4 w-4 text-t-primary mt-1 shrink-0" />
      <p className="flex-1 font-serif-display italic text-lg leading-snug" data-testid="diary-prompt-text">{p}</p>
      <button type="button" title="Another prompt" onClick={() => setP(randomPrompt(p))} data-testid="diary-prompt-shuffle" className="p-2 rounded-lg hover:bg-t-surface-hover text-t-muted"><Shuffle className="h-4 w-4" /></button>
      <button type="button" onClick={() => onUse(p)} data-testid="diary-prompt-use" className="text-xs font-medium text-t-primary hover:text-t-primary-hover whitespace-nowrap mt-2">Use this</button>
    </div>
  );
};

export const EntryDialog = ({ state, onClose, onSaved, headers }) => {
  const { user } = useAuth();
  const [form, setForm] = useState({});
  useEffect(() => {
    if (!state) return;
    const e = state.entry;
    setForm(e ? { title: e.title, content: e.content, entry_date: e.entry_date, prompt: e.prompt } : { title: "", content: "", entry_date: format(new Date(), "yyyy-MM-dd"), prompt: null });
  }, [state]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const applyPrompt = (p) => setForm((f) => ({ ...f, prompt: p, title: f.title || p }));
  const showPrompts = user.prefs?.diary_prompts !== false && !form.prompt;

  const save = async (e) => {
    e.preventDefault();
    if (!form.title?.trim()) return toast.error("Give your entry a title");
    try {
      const body = { ...form, title: form.title.trim() };
      if (state.entry) await api.patch(`/diary/entries/${state.entry.id}`, body, { headers });
      else await api.post("/diary/entries", body, { headers });
      toast.success("Entry saved");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const remove = async () => {
    await api.delete(`/diary/entries/${state.entry.id}`, { headers });
    toast("Entry deleted");
    onSaved();
    onClose();
  };

  return (
    <Dialog open={!!state} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="bg-t-bg border-t-line rounded-2xl sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="diary-entry-dialog">
        <DialogHeader><DialogTitle className="font-serif-display text-3xl font-semibold">{state?.entry ? "Edit entry" : "New entry"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-4">
          {showPrompts && <PromptPicker onUse={applyPrompt} />}
          {form.prompt && (
            <div className="flex items-start gap-2 pl-4 border-l-2 border-t-primary" data-testid="diary-entry-prompt">
              <p className="flex-1 font-serif-display italic text-lg text-t-c4">{form.prompt}</p>
              <button type="button" onClick={() => setForm((f) => ({ ...f, prompt: null }))} data-testid="diary-entry-prompt-remove" className="p-1 text-t-faint hover:text-t-ink"><X className="h-4 w-4" /></button>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_auto] gap-3">
            <input data-testid="diary-entry-title-input" className={`${inputCls} font-serif-display text-xl`} value={form.title || ""} onChange={set("title")} placeholder="Title" autoFocus />
            <input data-testid="diary-entry-date-input" type="date" className={inputCls} value={form.entry_date || ""} onChange={set("entry_date")} required />
          </div>
          <textarea data-testid="diary-entry-content-input" rows={12} className={`${inputCls} leading-relaxed`} value={form.content || ""} onChange={set("content")} placeholder="Write freely — this space is just for you..." />
          <div className="flex gap-3">
            {state?.entry && <button type="button" data-testid="diary-entry-delete" onClick={remove} className={`${btnSecondary} text-t-danger`}><Trash2 className="h-4 w-4" /> Delete</button>}
            <button type="submit" data-testid="diary-entry-save" className={`${btnPrimary} flex-1`}>Save entry</button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
